/**
 * Self-serve signup, served at /onboarding.
 *
 * A three-question stepper: name, e-mail, brand. One field on screen at a time,
 * which is the whole point of the design - the sub-heading asks the question and
 * the input is the only thing to do.
 *
 * Note what POST /api/register receives: `name` is the BRAND's, `contactName` is
 * the PERSON's. The collision between "your name" and "brand name" is easy to
 * misread, which is how the person's answer came to be thrown away: it never
 * left the browser, and onboarding step 1 then asked for it a second time and
 * threw that away too. Both are stored now, and step 1 is gone.
 *
 * @module web/screens/SignUpScreen
 */
import { useState } from "react";
import { post, setAuthToken } from "../api/client.ts";
import type { BrandStatusResponse } from "../api/types.ts";
import { DemoQuoteCard, readDemoQuote, Shell } from "./onboarding/Shell.tsx";

/**
 * What POST /api/register answers with.
 *
 * A union, because the two cases are genuinely different: a brand we have never
 * seen is created and signed in on the spot, and a brand we already know gets a
 * sign-in link in its INBOX and no token here. The route used to return a live
 * session for any address you could name - see its note in server.ts.
 */
export type RegisterResponse =
  | { returning: false; token: string; brand: BrandStatusResponse }
  | { returning: true; emailed: true };

/** The question above each step, in order. */
const PROMPTS = ["What's your name?", "Where can we reach you?", "And your brand?"] as const;

/** Signup is steps 1-3 of the whole flow; billing and card follow in OnboardingScreen. */
export const FLOW_STEPS = 5;

export function SignUpScreen({ onRegistered }: { onRegistered: (r: RegisterResponse) => void }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Set when the address was already ours and the link went to the inbox. */
  const [emailed, setEmailed] = useState(false);
  const [quote] = useState(() => readDemoQuote());

  const values = [name, email, brand];
  const canContinue = (values[step] ?? "").trim().length > 0;

  async function submit() {
    if (!name.trim() || !email.trim() || !brand.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // `name` here is the BRAND name - see the module note.
      const res = await post<RegisterResponse>("/api/register", {
        name: brand.trim(),
        email: email.trim(),
        contactName: name.trim(),
      });
      if (res.returning) {
        // Already ours. Nothing to sign in with - proving you hold the address
        // is the point, and the link is the proof.
        setEmailed(true);
        setBusy(false);
        return;
      }
      setAuthToken(res.token);
      // Keep the token in the address bar, as the original does: it is what
      // makes a reload of this page still be signed in before the cookie path
      // takes over.
      window.history.replaceState(null, "", `/?token=${res.token}`);
      onRegistered(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(false);
    }
  }

  if (emailed) {
    return (
      <Shell screen="signup-screen">
        <h1 className="ob2-h1">Check your <span>inbox</span></h1>
        <p className="ob2-sub">You already have an Aditor account. We sent a sign-in link to <b>{email.trim()}</b>.</p>
        <p className="ob2-note">It works for 30 days. Nothing after a minute? Check your spam folder.</p>
      </Shell>
    );
  }

  const FIELDS = [
    { id: "signup-name", type: "text", auto: "name", label: "Your name", value: name, set: setName, ph: "Jane Doe" },
    { id: "signup-email", type: "email", auto: "email", label: "Email", value: email, set: setEmail, ph: "jane@brand.com" },
    { id: "signup-brand", type: "text", auto: "organization", label: "Brand name", value: brand, set: setBrand, ph: "Your brand" },
  ] as const;
  const f = FIELDS[step]!;
  const last = step === FIELDS.length - 1;
  const next = () => { if (!canContinue || busy) return; if (last) void submit(); else setStep(step + 1); };

  return (
    <Shell screen="signup-screen" step={step + 1} of={FLOW_STEPS}>
      {step === 0 && <DemoQuoteCard quote={quote} />}
      <h1 className="ob2-h1">{step === 0 ? <>Welcome to <span>Aditor.</span></> : PROMPTS[step]}</h1>
      <p className="ob2-sub">{step === 0 ? PROMPTS[0] : step === 1 ? "Your sign-in link and every delivery land here." : "The name your ads run under."}</p>
      <form className="ob2-form" key={f.id} onSubmit={(e) => { e.preventDefault(); next(); }}>
        <label className="dm-sr" htmlFor={f.id}>{f.label}</label>
        <input id={f.id} className="ob2-input is-big" type={f.type} autoComplete={f.auto} autoFocus placeholder={f.ph}
          value={f.value} onChange={(e) => f.set(e.target.value)} />
        {error && <p className="ob2-error signup-error" role="alert">{error}</p>}
        <div className="ob2-actions">
          <button type="submit" className="dm-cta dm-cta-lg" disabled={busy || !canContinue}>
            {last ? (busy ? "Setting up…" : "Get Started") : "Continue"}
          </button>
          {step > 0 && <button type="button" className="dm-quiet" onClick={() => setStep(step - 1)}>Back</button>}
        </div>
      </form>
    </Shell>
  );
}
